import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiMapPin, FiLayers } from 'react-icons/fi';
import { Modal, Button, Badge } from './ui';
import { branchLabel } from '../utils/authorizedBranches';
import { roleLabel } from '../utils/roleLabel';
import { differingBranchRole } from './BranchRolesTable';

/**
 * Branch pick dialog:
 * - signin mode: shown when a multi-branch user logs in. The "All branches"
 *   option is the first row. The modal cannot be dismissed (no close button,
 *   Escape and backdrop clicks do nothing).
 * - create mode: shown when creating an unlinked record in All mode. No All
 *   option; has a Cancel button, and Escape/backdrop closes it.
 * Per-branch roles (switch on only): `allowAll={false}` drops the All row for
 * a user whose role differs between branches, and `defaultRole` labels each
 * branch whose role is not the default one.
 */
export default function BranchPickDialog({
  open,
  mode = 'create',
  branches = [],
  defaultValue,
  onPick,
  onCancel,
  allowAll = true,
  defaultRole = null,
}) {
  const { t } = useTranslation();
  const focusedRowRef = useRef(null);

  const isSignin = mode === 'signin';
  const showAll = isSignin && allowAll;
  const isCreate = mode === 'create';

  const isFocusedTarget = (target) => {
    if (showAll) {
      if (defaultValue === 'all') return target === 'all';
      if (branches.some((b) => b.id === defaultValue)) return target === defaultValue;
      return target === 'all';
    } else {
      if (branches.some((b) => b.id === defaultValue)) return target === defaultValue;
      return branches.length > 0 && target === branches[0].id;
    }
  };

  useEffect(() => {
    if (open && focusedRowRef.current) {
      focusedRowRef.current.focus();
    }
  }, [open, mode, defaultValue]);

  if (!open) return null;

  const title = isSignin ? t('mb.signinPickTitle') : t('mb.assistantPickBranch');
  const description = isSignin ? t('mb.signinPickHint') : undefined;

  const footer = isCreate ? (
    <Button variant="secondary" onClick={onCancel} type="button">
      {t('cancel')}
    </Button>
  ) : null;

  return (
    <Modal
      isOpen={open}
      onClose={isCreate ? onCancel : () => {}}
      title={title}
      description={description}
      size="sm"
      dismissible={isCreate}
      closeOnBackdrop={isCreate}
      initialFocus={focusedRowRef}
      footer={footer}
    >
      <div className="space-y-1">
        {showAll && (
          <>
            <button
              type="button"
              ref={isFocusedTarget('all') ? focusedRowRef : null}
              onClick={() => onPick && onPick('all')}
              className={`w-full text-start px-3 py-2.5 rounded-lg transition-colors mb-0.5 ${
                defaultValue === 'all'
                  ? 'bg-accent-soft text-accent-hover dark:text-accent font-medium'
                  : 'hover:bg-surface-2 text-content'
              }`}
            >
              <div className="flex items-center gap-3">
                <FiLayers
                  className={`shrink-0 ${defaultValue === 'all' ? 'text-accent' : 'text-content-muted'}`}
                  size={20}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{t('mb.allBranches')}</div>
                  <div className="text-xs text-content-muted">{t('mb.allBranchesHint')}</div>
                </div>
              </div>
            </button>
            <div className="my-1 border-t border-edge" />
          </>
        )}

        {branches.map((b) => {
          const isSelected = defaultValue === b.id;
          const label = branchLabel(b);
          const branchRole = differingBranchRole(b, defaultRole);
          return (
            <button
              key={b.id}
              type="button"
              ref={isFocusedTarget(b.id) ? focusedRowRef : null}
              onClick={() => onPick && onPick(b.id)}
              className={`w-full text-start px-3 py-2.5 rounded-lg transition-colors ${
                isSelected
                  ? 'bg-accent-soft text-accent-hover dark:text-accent font-medium'
                  : 'hover:bg-surface-2 text-content'
              }`}
            >
              <div className="flex items-center gap-3">
                {b.logo_url ? (
                  <img
                    src={b.logo_url}
                    alt={label}
                    className="w-7 h-7 rounded object-cover shrink-0"
                    style={{ width: '28px', height: '28px' }}
                  />
                ) : (
                  <FiMapPin
                    className={`shrink-0 ${isSelected ? 'text-accent' : 'text-content-muted'}`}
                    size={20}
                  />
                )}
                <div className="min-w-0 flex-1 flex items-center justify-between gap-2">
                  {branchRole ? (
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{label}</div>
                      <div className="text-xs text-content-muted truncate">
                        {t('ubr.roleInBranch', { role: roleLabel(branchRole) })}
                      </div>
                    </div>
                  ) : (
                    <div className="font-medium text-sm truncate">{label}</div>
                  )}
                  {b.is_home && (
                    <Badge size="sm" tone="slate" className="shrink-0">
                      {t('mb.homeBranch')}
                    </Badge>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </Modal>
  );
}
